/**
 * BatchesPage Component
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Page for managing inventory batches (purchases/shipments).
 * Provides batch list, shipment recording, and batch detail views.
 */

import { useState } from 'react'
import PageLayout from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { Plus, Package, Search, Filter } from 'lucide-react'
import { useInventoryBatches } from '@/hooks/useInventoryBatches'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import BatchListTable from '@/components/inventory/BatchListTable'
import BatchReceiptForm from '@/components/inventory/BatchReceiptForm'
import BatchDetailDrawer from '@/components/inventory/BatchDetailDrawer'

/**
 * BatchesPage Component
 *
 * @param {Object} props
 * @param {Object} props.user - Current user
 * @param {Function} props.onLogout - Logout callback
 */
export default function BatchesPage({ user, onLogout }) {
  // Data hooks
  const {
    batches,
    isLoading,
    error,
    itemFilter,
    statusFilter,
    recordShipment,
    updateBatch,
    deleteBatch,
    filterByItem,
    filterByStatus,
    refetch,
  } = useInventoryBatches()

  const { allItems } = useInventoryItems({ activeOnly: true })

  // UI state
  const [showReceiptForm, setShowReceiptForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedBatchId, setSelectedBatchId] = useState(null)
  const [showDetailDrawer, setShowDetailDrawer] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [batchToDelete, setBatchToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Handle shipment recording
  const handleRecordShipment = async (shipmentData) => {
    setIsSubmitting(true)
    try {
      const result = await recordShipment(shipmentData)
      toast.success('Shipment recorded', {
        description: `Batch ${result.data.batch_code} created with ${result.meta.converted_quantity} ${result.meta.unit_of_measure}`,
      })
      setShowReceiptForm(false)
    } catch (err) {
      toast.error('Failed to record shipment', {
        description: err.message,
      })
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle batch row click - open detail drawer
  const handleRowClick = (batch) => {
    setSelectedBatchId(batch.id)
    setShowDetailDrawer(true)
  }

  // Handle batch update
  const handleUpdateBatch = async (batchId, updates) => {
    setIsUpdating(true)
    try {
      await updateBatch(batchId, updates)
      toast.success('Batch updated')
    } catch (err) {
      toast.error('Failed to update batch', {
        description: err.message,
      })
      throw err
    } finally {
      setIsUpdating(false)
    }
  }

  // Handle delete confirmation
  const handleDeleteClick = (batch) => {
    setBatchToDelete(batch)
  }

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    if (!batchToDelete) return

    setIsDeleting(true)
    try {
      await deleteBatch(batchToDelete.id)
      toast.success('Batch deleted', {
        description: `Batch ${batchToDelete.batch_code} has been removed`,
      })
      setBatchToDelete(null)
    } catch (err) {
      toast.error('Failed to delete batch', {
        description: err.message,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Header actions: search → filters → primary action
  const headerActions = (
    <>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search batches..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 w-[200px]"
        />
      </div>

      {/* Item Filter */}
      {allItems.length > 0 && (
        <Select value={itemFilter || 'all'} onValueChange={(v) => filterByItem(v === 'all' ? null : v)}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Items" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            {allItems.map(item => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Status Filter */}
      <Select value={statusFilter || 'all'} onValueChange={(v) => filterByStatus(v === 'all' ? null : v)}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="available">Available</SelectItem>
          <SelectItem value="in_use">In Use</SelectItem>
          <SelectItem value="depleted">Depleted</SelectItem>
        </SelectContent>
      </Select>

      {/* Primary Action */}
      <Button onClick={() => setShowReceiptForm(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Record Shipment
      </Button>
    </>
  )

  return (
    <PageLayout
      user={user}
      onLogout={onLogout}
      title="Purchases"
      icon={Package}
      headerActions={headerActions}
    >
      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          <p className="font-medium">Error loading batches</p>
          <p className="text-sm">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Batch list table */}
      <BatchListTable
        batches={batches}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        onDelete={handleDeleteClick}
        searchQuery={searchQuery}
      />

      {/* Shipment receipt form dialog */}
      <BatchReceiptForm
        open={showReceiptForm}
        onOpenChange={setShowReceiptForm}
        onSubmit={handleRecordShipment}
        isSubmitting={isSubmitting}
      />

      {/* Batch detail drawer */}
      <BatchDetailDrawer
        open={showDetailDrawer}
        onOpenChange={setShowDetailDrawer}
        batchId={selectedBatchId}
        onUpdate={handleUpdateBatch}
        isUpdating={isUpdating}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!batchToDelete}
        onOpenChange={(open) => !open && setBatchToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Batch?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete batch{' '}
              <span className="font-mono font-medium">
                {batchToDelete?.batch_code}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  )
}
